import {
    Result,
    fetchAndDeserializeVersionedTransaction,
    fetchAndDeserializeVersionedTransactions,
    fetchAndDeserializeVersionedTransactionsWithSetup,
    fetchAndDeserializeVersionedTransactionsWithLoanInfo,
    deserializeVersionedTransaction
} from "@bridgesplit/utils";
import { VersionedTransaction } from "@solana/web3.js";

import {
    AbfTransactionHeaders,
    CloseStrategyTxnParams,
    TXN_STRATEGY_CREATE,
    WithdrawStrategyTxnParams,
    CreateCustomStrategyTxnParams,
    TXN_STRATEGY_UPDATE,
    EditCustomStrategyTxnParams,
    TXN_CREDITBOOK_CREATE,
    BorrowPrincipalTxnParams,
    TXN_CREDITBOOK_BORROW,
    TXN_CREDITBOOK_LENDER_BUILD,
    LenderBuildRequest,
    LenderBuildResponse,
    DepositStrategyTxnParams,
    TXN_STRATEGY_DEPOSIT,
    TXN_STRATEGY_CLOSE,
    TXN_STRATEGY_WITHDRAW,
    CreateLoanParams,
    ROUTE_LOAN_LUT,
    LoanLutQuery,
    SdkApiLoanInfo,
    CreateLoanReturn
} from "../types";
import { createMarketsInstance } from "./auth";

export async function getCreateStrategyTransaction(
    authentication: AbfTransactionHeaders,
    args: CreateCustomStrategyTxnParams
) {
    return await fetchAndDeserializeVersionedTransactionsWithSetup(
        createMarketsInstance(authentication),
        TXN_STRATEGY_CREATE,
        args
    );
}

export async function getDepositStrategyTransaction(
    authentication: AbfTransactionHeaders,
    args: DepositStrategyTxnParams
) {
    return [
        await fetchAndDeserializeVersionedTransaction(createMarketsInstance(authentication), TXN_STRATEGY_DEPOSIT, args)
    ];
}

export async function getWithdrawStrategyTransction(
    authentication: AbfTransactionHeaders,
    args: WithdrawStrategyTxnParams
) {
    return [
        await fetchAndDeserializeVersionedTransaction(
            createMarketsInstance(authentication),
            TXN_STRATEGY_WITHDRAW,
            args
        )
    ];
}

export async function getEditStrategyTransactions(
    authentication: AbfTransactionHeaders,
    args: EditCustomStrategyTxnParams
) {
    return await fetchAndDeserializeVersionedTransactions(
        createMarketsInstance(authentication),
        TXN_STRATEGY_UPDATE,
        args
    );
}

// custom fetch needed due to custom return type
export async function getCreateLoanTransaction(
    authentication: AbfTransactionHeaders,
    args: Omit<CreateLoanParams, "principalDecimals" | "collateralDecimals">
): Promise<Result<{ transaction: VersionedTransaction; transactions: VersionedTransaction[]; loanAddress: string }>> {
    try {
        const api = createMarketsInstance(authentication);

        const response = await api.post<CreateLoanReturn>(TXN_CREDITBOOK_CREATE, args);
        const transactionResponses = response.data.transactions?.length
            ? response.data.transactions
            : [response.data.transaction];
        const transactions = transactionResponses.map((transaction) => deserializeVersionedTransaction(transaction));
        const transaction = transactions[transactions.length - 1];
        if (!transaction) {
            throw new Error("Create loan response did not include any transactions");
        }

        return Result.ok({
            transaction,
            transactions,
            loanAddress: response.data.loanAddress
        });
    } catch (error) {
        return Result.err(error);
    }
}

export async function buildLenderTransactions(
    authentication: AbfTransactionHeaders,
    args: LenderBuildRequest
): Promise<VersionedTransaction[]> {
    const api = createMarketsInstance(authentication);
    const response = await api.post<LenderBuildResponse>(TXN_CREDITBOOK_LENDER_BUILD, args);
    return response.data.transactions.map((txn) => deserializeVersionedTransaction(txn));
}

export async function getBorrowPrincipalTransaction(
    authentication: AbfTransactionHeaders,
    args: BorrowPrincipalTxnParams
) {
    const { transactions } = await fetchAndDeserializeVersionedTransactionsWithLoanInfo<
        BorrowPrincipalTxnParams,
        SdkApiLoanInfo
    >(createMarketsInstance(authentication), TXN_CREDITBOOK_BORROW, args);
    return transactions;
}

export async function getCreateLoanLutTransaction(authentication: AbfTransactionHeaders, args: LoanLutQuery) {
    return [await fetchAndDeserializeVersionedTransaction(createMarketsInstance(authentication), ROUTE_LOAN_LUT, args)];
}

export async function getCloseStrategyTransaction(authentication: AbfTransactionHeaders, args: CloseStrategyTxnParams) {
    return [
        await fetchAndDeserializeVersionedTransaction(
            createMarketsInstance(authentication),
            `${TXN_STRATEGY_CLOSE}/${args.strategyAddress}`
        )
    ];
}
